/* ==========================================================================
   <hero-slideshow> — drives the Product hero slideshow section.

   Reads its data-* attributes for behaviour, manages the slides / arrows /
   dots the section renders, and handles fade or slide transitions, autoplay
   (with pause on hover, focus, tab-away and off-screen), keyboard, swipe and
   reduced-motion.

   Loaded as: <script src="component-hero-slideshow.js" type="module" defer>
   ========================================================================== */

class HeroSlideshow extends HTMLElement {
  connectedCallback() {
    if (this._ready) return;
    this._ready = true;

    this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-hero-dot]'));
    this.prevBtn = this.querySelector('[data-hero-prev]');
    this.nextBtn = this.querySelector('[data-hero-next]');
    this.index = 0;
    this.count = this.slides.length;
    if (this.count <= 1) return; // nothing to animate

    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.getAttribute('data-autoplay') === 'true' && !this.reduce;
    this.speed = parseInt(this.getAttribute('data-autoplay-speed'), 10) || 5000;
    this.pauseOnHover = this.getAttribute('data-pause-on-hover') === 'true';
    this.timer = null;
    this.paused = false;

    this._bind();
    this._show(0, 0);
    if (this.autoplay) this._start();
  }

  disconnectedCallback() { this._stop(); }

  _bind() {
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._go(this.index + 1, true));
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._go(this.index - 1, true));

    this.dots.forEach((dot, i) =>
      dot.addEventListener('click', () => this._go(i, true))
    );

    // Keyboard when focus is inside the slideshow
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); this._go(this.index + 1, true); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); this._go(this.index - 1, true); }
    });

    // Pause conditions. Hover pause is mouse-only: a touch fires pointerenter
    // too, and its pointerleave is not guaranteed, which would leave autoplay
    // stopped for good after a single tap on a phone.
    if (this.pauseOnHover) {
      this.addEventListener('pointerenter', (e) => {
        if (e.pointerType === 'mouse') this.paused = true;
      });
      this.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') this.paused = false;
      });
      this.addEventListener('focusin', () => { this.paused = true; });
      this.addEventListener('focusout', (e) => {
        if (!this.contains(e.relatedTarget)) this.paused = false;
      });
    }
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { this._stop(); return; }
      if (this.autoplay) this._start();
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver((entries) => {
        entries.forEach((en) => { this.paused = !en.isIntersecting; });
      }, { threshold: 0.2 }).observe(this);
    }

    // Touch swipe
    let sx = 0, sy = 0, tracking = false, swiped = false;
    this.addEventListener('touchstart', (e) => {
      swiped = false; // clears the flag left by a swipe that never produced a click
      if (e.touches.length > 1) { tracking = false; return; } // pinch-zoom, not a swipe
      sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
    }, { passive: true });
    this.addEventListener('touchend', (e) => {
      if (!tracking) return;
      tracking = false;
      const dx = e.changedTouches[0].clientX - sx;
      const dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
        swiped = true;
        this._go(this.index + (dx < 0 ? 1 : -1), true);
      }
    }, { passive: true });

    // A swipe that starts on the whole-slide link (or a button) still ends in a
    // click on some mobile browsers — swallow that one click so a swipe never
    // navigates.
    this.addEventListener('click', (e) => {
      if (!swiped) return;
      swiped = false;
      e.preventDefault();
      e.stopPropagation();
    }, true);

    // Editor re-render safety
    document.addEventListener('shopify:section:select', () => this._stop());
    document.addEventListener('shopify:section:deselect', () => { if (this.autoplay) this._start(); });
  }

  _show(next, prev) {
    this.slides.forEach((slide, i) => {
      const active = i === next;
      slide.classList.toggle('is-active', active);
      slide.classList.toggle('is-prev', i === prev && !active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      // Keep the off-screen slides' links and buttons out of the tab order and
      // out of the screen reader's swipe order.
      slide.inert = !active;
    });
    this.dots.forEach((dot, i) => {
      const active = i === next;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  _go(to, manual) {
    const prev = this.index;
    // wrap around
    this.index = (to + this.count) % this.count;
    this._show(this.index, prev);
    if (manual && this.autoplay) this._start(); // reset the clock after a manual move
  }

  _start() {
    this._stop();
    this.timer = setInterval(() => {
      if (this.paused || document.hidden) return;
      this._go(this.index + 1, false);
    }, this.speed);
  }

  _stop() { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
}

if (!customElements.get('hero-slideshow')) {
  customElements.define('hero-slideshow', HeroSlideshow);
}